import { criteria, prizes } from "../data/program";
import type { CriterionScore, GroupScore } from "../types/hackathon";

const rationale: Record<string, [string, string, string]> = {
  problem: [
    "Clear, urgent problem backed by user evidence and a sizeable market.",
    "Problem is relevant but the market sizing and evidence are thin.",
    "Problem statement is vague and not yet validated with users.",
  ],
  innovation: [
    "Distinctive approach with a credible edge over existing products.",
    "Solid idea, though similar solutions exist and the edge is modest.",
    "Concept closely mirrors existing products without a clear twist.",
  ],
  prototype: [
    "Working prototype demonstrated live; core function is convincing.",
    "Partial prototype — key subsystems work but integration is incomplete.",
    "Prototype is mostly conceptual; little working hardware shown.",
  ],
  business: [
    "Pricing, unit economics and go-to-market are well reasoned.",
    "Revenue model is plausible but assumptions need testing.",
    "Business model is unclear or relies on untested assumptions.",
  ],
  pitch: [
    "Compelling story, tight timing and confident Q&A.",
    "Clear pitch with a few gaps in flow or Q&A answers.",
    "Pitch was hard to follow and ran over or under time.",
  ],
};

const round1 = (n: number) => Math.round(n * 10) / 10;
const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

/**
 * Simulated evaluation used until a live AI provider is connected.
 * Scores lean on the team's recorded progress so preparation is rewarded.
 */
export function simulateEvaluation(progressPercent: number): GroupScore {
  const scores: Record<string, CriterionScore> = {};
  criteria.forEach((c) => {
    const base = 5.4 + (progressPercent / 100) * 3;
    const score = round1(clamp(base + (Math.random() * 2.6 - 1.1), 1, 10));
    const tier = score >= 8 ? 0 : score >= 6 ? 1 : 2;
    scores[c.id] = { score, rationale: rationale[c.id][tier] };
  });
  const total = round1(criteria.reduce((sum, c) => sum + (scores[c.id].score / 10) * c.weight, 0));
  const best = [...criteria].sort((a, b) => scores[b.id].score - scores[a.id].score)[0];
  const weakest = [...criteria].sort((a, b) => scores[a.id].score - scores[b.id].score)[0];
  return {
    criteria: scores,
    total,
    summary: `Strongest on ${best.label.toLowerCase()}; most room to grow in ${weakest.label.toLowerCase()}.`,
    evaluatedAt: new Date().toISOString(),
  };
}

export function rankGroups(scores: Record<number, GroupScore>) {
  return Object.entries(scores)
    .map(([id, s]) => ({ groupId: Number(id), total: s.total }))
    .sort((a, b) => b.total - a.total)
    .map((row, i) => ({ ...row, place: i + 1, prize: prizes[i] }));
}

export function toEmbedUrl(url: string): string {
  const trimmed = url.trim();
  const watch = trimmed.match(/youtube\.com\/watch\?v=([\w-]+)/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  const live = trimmed.match(/youtube\.com\/live\/([\w-]+)/);
  if (live) return `https://www.youtube.com/embed/${live[1]}`;
  const short = trimmed.match(/youtu\.be\/([\w-]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  return trimmed;
}
